import React, { useState, useEffect } from 'react';
import {
  Modal,
  Descriptions,
  Typography,
  InputNumber,
  Button,
  Space,
  message,
  Divider,
} from 'antd';
import { supabase } from '../../lib/supabase';
import type { WmsManualOrder } from '../../types/wms';

interface ManualOrderDetailModalProps {
  order: WmsManualOrder | null;
  open: boolean;
  onClose: () => void;
  onQuoteSubmitted?: () => void;
}

export const ManualOrderDetailModal: React.FC<ManualOrderDetailModalProps> = ({
  order,
  open,
  onClose,
  onQuoteSubmitted,
}) => {
  const [quoteAmount, setQuoteAmount] = useState<number | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && order) {
      setQuoteAmount(order.admin_quote_amount != null ? Number(order.admin_quote_amount) : undefined);
    }
  }, [open, order]);

  const handleSubmitQuote = async () => {
    if (!order) return;
    if (quoteAmount == null || quoteAmount < 0) {
      message.warning('Enter a quote amount.');
      return;
    }
    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('wms_manual_orders')
        .update({
          admin_quote_amount: quoteAmount,
          admin_quoted_at: new Date().toISOString(),
          admin_quoted_by_user_id: user?.id ?? null,
          status: 'pending_customer_approval',
        })
        .eq('id', order.id);

      if (error) throw error;
      message.success('Quote submitted. Customer can now approve.');
      onQuoteSubmitted?.();
      onClose();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to submit quote');
    } finally {
      setSubmitting(false);
    }
  };

  if (!order) return null;

  const products = Array.isArray(order.products) ? order.products : [];
  const deliveryLine = [
    order.deliveryname,
    [order.deliveryaddress, order.deliveryaddress2].filter(Boolean).join(', '),
    [order.deliveryzipcode, order.deliverycity].filter(Boolean).join(' '),
    order.deliverycountry,
  ]
    .filter(Boolean)
    .join(', ') || '—';

  return (
    <Modal
      title={`Manual order #${order.id}`}
      open={open}
      onCancel={onClose}
      footer={
        order.status === 'awaiting_quote' ? (
          <Space>
            <Button onClick={onClose}>Close</Button>
            <Button type="primary" loading={submitting} onClick={handleSubmitQuote}>
              Submit quote
            </Button>
          </Space>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )
      }
      width={640}
      destroyOnClose
    >
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="ID">#{order.id}</Descriptions.Item>
        <Descriptions.Item label="Company ID">{order.company_id}</Descriptions.Item>
        <Descriptions.Item label="Reference">{order.reference ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Status">{order.status}</Descriptions.Item>
        <Descriptions.Item label="Shipment size">{order.shipment_size}</Descriptions.Item>
        <Descriptions.Item label="Created">{new Date(order.created_at).toLocaleString()}</Descriptions.Item>
        <Descriptions.Item label="Updated">{new Date(order.updated_at).toLocaleString()}</Descriptions.Item>
      </Descriptions>

      <Divider orientation="left">Delivery</Divider>
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="Name">{order.deliveryname ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Address">{deliveryLine}</Descriptions.Item>
        <Descriptions.Item label="Country">{order.deliverycountry ?? '—'}</Descriptions.Item>
      </Descriptions>

      <Divider orientation="left">Contact</Divider>
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="Email">{order.emailaddress ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Telephone">{order.telephone ?? '—'}</Descriptions.Item>
      </Descriptions>

      {order.customer_remarks && (
        <>
          <Divider orientation="left">Remarks</Divider>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
            {order.customer_remarks}
          </Typography.Paragraph>
        </>
      )}

      <Divider orientation="left">Products</Divider>
      {products.length === 0 ? (
        <Typography.Text type="secondary">No products</Typography.Text>
      ) : (
        <Descriptions column={1} size="small" bordered>
          {products.map((p, i) => (
            <Descriptions.Item key={i} label={p.productcode ?? `Product ${i + 1}`}>
              {p.amount} × {p.name ?? p.productcode ?? p.idproduct ?? '—'}
            </Descriptions.Item>
          ))}
        </Descriptions>
      )}

      <Divider orientation="left">Pricing & quote</Divider>
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="Requested shipment price">
          {order.requested_shipment_price != null ? `€${Number(order.requested_shipment_price).toFixed(2)}` : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Admin quote">
          {order.status === 'awaiting_quote' ? (
            <InputNumber
              min={0}
              step={0.01}
              placeholder="Amount (€)"
              value={quoteAmount}
              onChange={(v) => setQuoteAmount(v ?? undefined)}
              style={{ width: 140 }}
            />
          ) : order.admin_quote_amount != null ? (
            `€${Number(order.admin_quote_amount).toFixed(2)}`
          ) : (
            '—'
          )}
        </Descriptions.Item>
        {order.admin_quoted_at && (
          <Descriptions.Item label="Quoted at">{new Date(order.admin_quoted_at).toLocaleString()}</Descriptions.Item>
        )}
      </Descriptions>

      {(order.wms_order_id != null || order.wms_order_external_id) && (
        <>
          <Divider orientation="left">WMS</Divider>
          <Descriptions column={1} size="small" bordered>
            {order.wms_order_id != null && (
              <Descriptions.Item label="WMS order ID">{order.wms_order_id}</Descriptions.Item>
            )}
            {order.wms_order_external_id && (
              <Descriptions.Item label="WMS order ref">{order.wms_order_external_id}</Descriptions.Item>
            )}
            {order.wms_processed_at && (
              <Descriptions.Item label="Processed at">{new Date(order.wms_processed_at).toLocaleString()}</Descriptions.Item>
            )}
          </Descriptions>
        </>
      )}
    </Modal>
  );
};
